import { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, XCircle, BookMarked } from "lucide-react";
import { api, errMsg, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Provenance, Status, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const LABEL = { applicant_summary: "Applicant summary", respondent_summary: "Respondent summary", member_brief: "Neutral Member brief" };

export default function CaseIQTab({ c }) {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState(null);
  const [job, setJob] = useState(null);
  const load = () => api.get(`/caseiq/case/${c.id}`).then((r) => { setData(r.data); setKind((k) => k || r.data.visible_kinds[0]); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get(`/caseiq/case/${c.id}/jobs`).then((r) => r.data[0] && setJob(r.data[0])).catch(() => {}); }, [c.id]); // eslint-disable-line

  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return;
    const t = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/caseiq/jobs/${job.id}`);
        setJob(j);
        if (j.status === "done") { toast.success("CaseIQ output ready — awaiting human review"); setKind(j.kind); load(); }
        if (j.status === "failed") toast.error(`CaseIQ failed: ${j.error}`);
      } catch { /* keep polling */ }
    }, 1000);
    return () => clearInterval(t);
  }, [job]); // eslint-disable-line

  const generate = async () => {
    try { const { data: j } = await api.post(`/caseiq/case/${c.id}/generate`, { kind }); setJob(j); toast.info("CaseIQ analysis started in the background"); } catch (e) { toast.error(errMsg(e)); }
  };
  const busy = job && ["queued", "running"].includes(job.status);
  const [bundling, setBundling] = useState(false);
  const bundle = async () => {
    setBundling(true);
    try {
      const { data: d } = await api.post(`/cases/${c.id}/hearing-bundle`);
      toast.success(`${d.filename} — ${d.page_count} pages (brief ${d.bundle_contents.brief_id ? "included" : "missing"}, tribunal book ${d.bundle_contents.tribunal_book_id ? "included" : "missing"})`);
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); } finally { setBundling(false); }
  };
  const review = async (id, approve) => {
    try { await api.post(`/caseiq/${id}/review`, { approve, note: approve ? "Reviewed and accepted" : "Rejected by reviewer" }); toast.success(approve ? "Marked reviewed" : "Rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <div className="text-sm text-slate-500 mt-4">Loading CaseIQ…</div>;
  const outs = data.outputs.filter((o) => o.kind === kind);

  return (
    <div className="space-y-4 mt-4" data-testid="caseiq-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-white border border-slate-200 rounded p-1">
          {data.visible_kinds.map((k) => <button key={k} onClick={() => setKind(k)} data-testid={`caseiq-kind-${k}`} className={`text-xs px-3 py-1.5 rounded ${kind === k ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{LABEL[k]}</button>)}
        </div>
        {can("caseiq.member_brief") && <Button variant="outline" className="border-blue-300 text-blue-900" onClick={bundle} disabled={bundling} data-testid="hearing-bundle-btn"><BookMarked className="h-4 w-4 mr-1" />{bundling ? "Compiling bundle…" : "Hearing-day bundle"}</Button>}
        {can("caseiq.generate") && <Button className="bg-indigo-700 hover:bg-indigo-800" onClick={generate} disabled={busy} data-testid="caseiq-generate-btn"><Sparkles className="h-4 w-4 mr-1" />{busy ? "Analysing in background…" : "Generate with CaseIQ"}</Button>}
      </div>
      {job && job.status !== "done" && (
        <div className={`panel px-4 py-3 ${job.status === "failed" ? "border-rose-200" : "border-indigo-200"}`} data-testid="caseiq-job-progress">
          <div className="flex items-center justify-between text-xs"><span className="font-medium text-slate-800">{LABEL[job.kind]} · {job.stage}</span><span className="mono" data-testid="caseiq-job-percent">{job.progress}%</span></div>
          <div className="h-1.5 bg-slate-200 rounded mt-2 overflow-hidden"><div className={`h-full transition-[width] duration-700 ${job.status === "failed" ? "bg-rose-500" : "bg-indigo-600"}`} style={{ width: `${job.progress}%` }} /></div>
          <div className="text-[11px] text-slate-500 mt-1">{job.status === "failed" ? job.error : "You can navigate away; the output will appear here when ready."}</div>
          {job.partial_text && <div className="mt-3 border-t border-indigo-100 pt-2"><div className="eyebrow mb-1">Live preview · {job.partial_chars} chars streamed · not yet reviewed</div><div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed" data-testid="caseiq-live-preview">{job.partial_text}<span className="inline-block w-1.5 h-4 bg-indigo-600 ml-0.5 align-text-bottom animate-pulse" /></div></div>}
        </div>)}
      <p className="text-xs text-slate-500">CaseIQ is source-grounded: it only uses documents on the file (ADR-confidential material excluded), cites them by tag, never cites authorities absent from sources and never expresses a view on liability. Every output is amber-lane: it must be reviewed by an authorised human.</p>
      {outs.length === 0 && <Panel><Empty text="No output yet for this view. Generate one with CaseIQ." /></Panel>}
      {outs.map((o) => (
        <Panel key={o.id} testId={`caseiq-output-${o.id}`} title={<span className="flex items-center gap-2">{LABEL[o.kind]} <Status value={o.status} /></span>}
          action={can("caseiq.review") && o.status === "awaiting_review" && (
            <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => review(o.id, false)} data-testid={`caseiq-reject-${o.id}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => review(o.id, true)} data-testid={`caseiq-approve-${o.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Mark reviewed</Button></div>)}>
          <Provenance out={o} />
          <h3 className="text-lg font-semibold text-slate-900 mt-4">{o.content.headline}</h3>
          <div className="text-sm text-slate-700 leading-relaxed mt-2 whitespace-pre-line">{o.content.summary}</div>
          <div className="grid md:grid-cols-3 gap-4 mt-5">
            <Block title="Key points" items={o.content.key_points} />
            <Block title="Procedural issues" items={o.content.procedural_issues} tone="amber" />
            <Block title="Open questions" items={o.content.open_questions} />
          </div>
          {(o.content.caveats?.length > 0 || o.content.authorities_cited !== undefined) && (
            <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
              <div><span className="font-semibold">Authorities cited:</span> {o.content.authorities_cited?.length ? o.content.authorities_cited.join("; ") : "none (none present in sources)"}</div>
              {o.content.caveats?.map((cv, i) => <div key={i}>Caveat: {cv}</div>)}
              <div className="mono mt-1">prompt hash {o.prompt_hash} · guardrails {o.guardrails_version} · {titleCase(o.triage)} lane</div>
            </div>)}
        </Panel>))}
    </div>
  );
}

function Block({ title, items = [], tone }) {
  return (
    <div><div className="eyebrow mb-1.5">{title}</div>
      <ul className="space-y-1">{items.length === 0 && <li className="text-xs text-slate-400">None</li>}{items.map((it, i) => <li key={i} className={`text-sm pl-3 border-l-2 ${tone === "amber" ? "border-amber-400" : "border-slate-300"}`}>{it}</li>)}</ul></div>
  );
}
